/** Mirror of the sidecar wire protocol (dunea-desktop packages/sidecar/src/protocol.ts). */

export const REMOTE_PROTO = 1;
export const REMOTE_PROTO_MIN = 1;

export interface ProjectMeta {
  id: string;
  path: string;
  name: string;
  defaultBaseBranch?: string;
  addedAt: number;
  lastOpenedAt: number;
}

export type SetupStatus = 'none' | 'running' | 'ok' | 'failed';

export interface WorktreeMeta {
  id: string;
  projectId: string;
  path: string;
  branch: string;
  baseBranch: string;
  createdAt: number;
  setupStatus: SetupStatus;
  isPrimary?: boolean;
  name?: string;
  pendingTitle?: boolean;
  issue?: { label: string; url: string };
}

export type SessionStatus = 'starting' | 'working' | 'awaiting_permission' | 'idle' | 'exited' | 'error';

export interface SessionRow {
  id: string;
  worktreeId: string;
  claudeSessionId: string | null;
  title: string;
  permissionMode: string;
  model?: string;
  createdAt: number;
  lastActiveAt: number;
  status: SessionStatus;
  contextTokens?: number;
}

export interface CheckpointInfo {
  ref: string;
  headSha: string;
  uuid?: string;
}

export type ChatItem = { seq?: number } & (
  | { kind: 'user'; id: string; text: string; ts: number }
  | { kind: 'assistant_text'; id: string; text: string; ts: number; parentToolUseId?: string | null }
  | { kind: 'thinking'; id: string; text: string; ts: number; parentToolUseId?: string | null }
  | {
      kind: 'tool_use';
      id: string;
      toolUseId: string;
      name: string;
      input: Record<string, unknown>;
      ts: number;
      result?: { text: string; isError: boolean; truncated?: boolean };
      parentToolUseId?: string | null;
    }
  | {
      kind: 'turn_end';
      id: string;
      ts: number;
      costUsd?: number;
      durationMs?: number;
      isError?: boolean;
      resultText?: string;
      checkpoint?: CheckpointInfo;
    }
  | { kind: 'compact_boundary'; id: string; ts: number; preTokens: number; trigger: 'manual' | 'auto' }
  | { kind: 'error'; id: string; text: string; ts: number }
);

export interface PermissionRequest {
  requestId: string;
  sessionId: string;
  toolName: string;
  input: Record<string, unknown>;
  canAlwaysAllow?: boolean;
}

export type AttentionEntry =
  | { kind: 'session'; sessionId: string; worktreeId: string; reason: 'permission' | 'error' | 'done'; ts: number }
  | { kind: 'pr'; worktreeId: string; number?: number; title?: string; ts: number };

export interface RemoteDevice {
  id: string;
  name: string;
  platform: string;
  role: string;
  publicKey: string;
  state: string;
  fingerprint: string;
  lastSeenAt?: string | null;
  createdAt?: string;
}

export interface Snapshot {
  attention: AttentionEntry[];
  worktrees: WorktreeMeta[];
  sessions: SessionRow[];
  projects: ProjectMeta[];
  ts: number;
}

export interface TranscriptPage {
  gen: number;
  seq: number;
  items: ChatItem[];
  done: boolean;
  resync: boolean;
  status: SessionStatus;
  pendingPermissions: PermissionRequest[];
}

export interface DuneaUser {
  id: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
}

/** Fork-point review payload (Changes tab). */
export interface CommittedChange {
  path: string;
  status: string;
}
export interface CommitInfo {
  hash: string;
  shortHash: string;
  subject: string;
  author: string;
  ts: number;
}
export interface BranchReview {
  base: string;
  changes: CommittedChange[];
  commits: CommitInfo[];
}

export interface PrInfo {
  exists: boolean;
  number?: number;
  url?: string;
  title?: string;
  state?: string;
  baseRefName?: string;
  mergeable?: string;
  mergeStateStatus?: string;
  reviewDecision?: string;
  checks?: 'passing' | 'failing' | 'pending' | null;
  unresolvedThreads?: number;
}

export interface PrCheck {
  name: string;
  bucket: 'pass' | 'fail' | 'pending' | 'skipping' | 'cancel';
  link?: string;
  workflow?: string;
}

export interface PrComment {
  author: string;
  body: string;
  createdAt: string;
  state?: string;
  path?: string;
  line?: number;
  url?: string;
  kind: 'comment' | 'review' | 'inline';
}

export interface PrDetail {
  checks: PrCheck[];
  comments: PrComment[];
}
